/**
 * In-memory fakes for the P14-04 seams — {@link SuppressionPort} and {@link OptOutPort}.
 *
 * The two claims these exist to let a consumer exercise before the routes land are the two
 * that are easy to get wrong and impossible to see afterwards:
 *
 *   **AC-30's one second.** `captureRefusal` reads the injected clock, so a suite can advance
 *   it by 1,200 ms and watch the receipt come back `after_deadline` with the row still
 *   written. `writeLatencyMs` is settable for exactly that: a fake that was always instant
 *   could only ever prove the happy path.
 *
 *   **Operators add, never remove.** `removeDoNotCallEntry` takes a {@link
 *   SuppressionRemover}, which has no operator arm, so the refusal is a compile error rather
 *   than a runtime one. What this fake adds is the *other* half — an entry an operator added
 *   is removable by a member, which is the behaviour the design intends and which a guard
 *   written as "reject operator removals" would have got wrong by also rejecting these.
 */
import { doNotCallEntryId as doNotCallEntryIdCodec } from "../../id/codec.js";
import { judgeRefusalCapture } from "../../compliance/refusal.js";
import { isOptOutBusinessSlug, isOptOutNumber } from "../../compliance/opt-out.js";
import { parseLanguageCode } from "../../template/language-code.js";
/**
 * The phrases the design's Do-not-call card lists, in the four languages it shows them in.
 *
 * A fixture, not the product's list: the real one is a language-pack deliverable for all 26
 * languages (Track L), and an incomplete pack makes a language unavailable rather than
 * falling back to English.
 */
export const FAKE_REFUSAL_PHRASE = Object.freeze([
    {
        languageCode: parseLanguageCode("en"),
        phrase: ["Take me off your list", "Do not call me again", "Stop calling me"],
    },
    {
        languageCode: parseLanguageCode("es"),
        phrase: ["No me llames más", "Quítame de la lista"],
    },
    {
        languageCode: parseLanguageCode("it"),
        phrase: ["Basta chiamate", "Non chiamatemi più"],
    },
    {
        languageCode: parseLanguageCode("el"),
        phrase: ["Μη με ξανακαλέσετε"],
    },
]);
function matchesFilter(entry, query) {
    if (query.filter === "said_on_call" && entry.source !== "said_on_call")
        return false;
    if (query.filter === "opt_out_page" && entry.source !== "opt_out_page")
        return false;
    if (query.filter === "added_by_member" &&
        entry.source !== "added_by_member" &&
        entry.source !== "added_by_operator") {
        return false;
    }
    const needle = query.search.trim().toLowerCase();
    if (needle === "")
        return true;
    return (entry.e164.toLowerCase().includes(needle) ||
        (entry.displayName ?? "").toLowerCase().includes(needle));
}
export class FakeSuppressionPort {
    #deps;
    #entry = new Map();
    /**
     * How long the fake pretends the suppression write takes.
     *
     * Zero by default. A suite that wants to prove the AC-30 breach path sets it above
     * `REFUSAL_SUPPRESSION_DEADLINE_MS`; a suite that wants the happy path leaves it alone.
     */
    writeLatencyMs = 0;
    /** When set, the fake pretends the conversation ended this long after recognition. */
    conversationEndsAfterMs = null;
    constructor(deps) {
        this.#deps = deps;
    }
    #rowsOf(accountId) {
        const found = this.#entry.get(accountId);
        if (found !== undefined)
            return found;
        const made = [];
        this.#entry.set(accountId, made);
        return made;
    }
    #currentEntry(accountId) {
        return this.#rowsOf(accountId).filter((one) => one.removedAt === null);
    }
    #insert(scope, draft, at) {
        const author = draft.author;
        const made = {
            accountId: scope.accountId,
            doNotCallEntryId: doNotCallEntryIdCodec.next(this.#deps.clock, this.#deps.random),
            e164: draft.e164,
            e164Hash: `hash:${draft.e164}`,
            displayName: draft.displayName,
            spokenWords: draft.spokenWords,
            source: draft.source,
            scope: draft.scope,
            voiceAgentId: draft.scope === "one_voice_agent" ? draft.voiceAgentId : null,
            conversationId: author.kind === "party" ? author.conversationId : null,
            addedByPersonId: author.kind === "person" ? author.personId : null,
            addedByOperatorId: author.kind === "operator" ? author.operatorId : null,
            addedAt: at,
            removedAt: null,
            removedByPersonId: null,
        };
        this.#rowsOf(scope.accountId).push(made);
        return made;
    }
    listDoNotCallEntries(scope, query) {
        const current = this.#currentEntry(scope.accountId);
        const matched = current.filter((one) => matchesFilter(one, query));
        const weekAgo = this.#deps.clock.now() - 7 * 86_400_000;
        const page = {
            entry: matched,
            nextCursor: null,
            totalCount: current.length,
            addedThisWeekCount: current.filter((one) => one.addedAt.getTime() >= weekAgo).length,
            skippedConversationCount: 0,
            // AC-31's proof figure. A fake that reported anything else would be reporting a defect
            // it does not have.
            dialledInErrorCount: 0,
        };
        return Promise.resolve(page);
    }
    isSuppressed(scope, e164, voiceAgentId) {
        return Promise.resolve(this.#currentEntry(scope.accountId).some((one) => one.e164 === e164 &&
            (one.scope === "every_voice_agent" || one.voiceAgentId === voiceAgentId)));
    }
    addDoNotCallEntry(scope, draft) {
        return Promise.resolve(this.#insert(scope, draft, new Date(this.#deps.clock.now())));
    }
    removeDoNotCallEntry(scope, doNotCallEntryId, remover) {
        const rows = this.#rowsOf(scope.accountId);
        const at = rows.findIndex((one) => one.doNotCallEntryId === doNotCallEntryId);
        if (at < 0) {
            return Promise.reject(new Error(`No do_not_call_entry ${doNotCallEntryId} on account ${scope.accountId}. ` +
                `Suppression is per account; an entry on another account is not visible here.`));
        }
        const found = rows[at];
        if (found === undefined)
            return Promise.resolve();
        rows[at] = {
            ...found,
            removedAt: new Date(this.#deps.clock.now()),
            removedByPersonId: remover.personId,
        };
        return Promise.resolve();
    }
    importDoNotCallEntries(scope, uploadHandle, author) {
        // The handle stands in for a parsed upload; the fake treats it as one number per line.
        const at = new Date(this.#deps.clock.now());
        let addedCount = 0;
        let alreadyPresentCount = 0;
        for (const line of uploadHandle.split("\n")) {
            const e164 = line.trim();
            if (e164 === "")
                continue;
            if (this.#currentEntry(scope.accountId).some((one) => one.e164 === e164)) {
                alreadyPresentCount += 1;
                continue;
            }
            this.#insert(scope, {
                e164,
                displayName: null,
                source: author.kind === "operator" ? "added_by_operator" : "added_by_member",
                scope: "every_voice_agent",
                voiceAgentId: null,
                spokenWords: null,
                author,
            }, at);
            addedCount += 1;
        }
        return Promise.resolve({ addedCount, alreadyPresentCount });
    }
    captureRefusal(scope, request) {
        const suppressedAt = new Date(request.recognisedAt.getTime() + this.writeLatencyMs);
        const conversationEndedAt = this.conversationEndsAfterMs === null
            ? null
            : new Date(request.recognisedAt.getTime() + this.conversationEndsAfterMs);
        const made = this.#insert(scope, {
            e164: request.e164,
            displayName: null,
            source: "said_on_call",
            scope: "every_voice_agent",
            voiceAgentId: null,
            spokenWords: request.spokenWords,
            author: { kind: "party", conversationId: request.conversationId },
        }, suppressedAt);
        return Promise.resolve({
            outcome: judgeRefusalCapture({
                recognisedAt: request.recognisedAt,
                suppressedAt,
                conversationEndedAt,
            }),
            doNotCallEntryId: made.doNotCallEntryId,
        });
    }
    listRefusalPhrases(_scope) {
        return Promise.resolve(FAKE_REFUSAL_PHRASE);
    }
}
/**
 * The public page's fake (AC-32).
 *
 * The registry is a slug-to-target map, and `submit` writes through {@link
 * FakeSuppressionPort} on the target's account. There is no code path that reaches a second
 * account, which is the property AC-32 is about: it is not enforced here, it is absent.
 */
export class FakeOptOutPort {
    #target = new Map();
    #suppression;
    #deps;
    /** Numbers the fake refuses as rate-limited, so the third state is reachable in a suite. */
    rateLimited = new Set();
    constructor(suppression, deps) {
        this.#suppression = suppression;
        this.#deps = deps;
    }
    register(target) {
        if (!isOptOutBusinessSlug(target.businessSlug)) {
            throw new RangeError(`"${target.businessSlug}" is not a business slug.`);
        }
        this.#target.set(target.businessSlug, target);
    }
    resolveTarget(businessSlug) {
        const found = this.#target.get(businessSlug);
        return Promise.resolve(found === undefined ? { found: false } : { found: true, target: found });
    }
    async submit(submission, _sourceIpHash) {
        if (!this.#target.has(submission.target.businessSlug)) {
            return { accepted: false, refusal: "unknown_business_slug" };
        }
        if (!isOptOutNumber(submission.e164)) {
            return { accepted: false, refusal: "invalid_number" };
        }
        if (this.rateLimited.has(submission.e164)) {
            return { accepted: false, refusal: "rate_limited" };
        }
        const suppressedAt = new Date(this.#deps.clock.now());
        const made = await this.#suppression.addDoNotCallEntry({ accountId: submission.target.accountId }, {
            e164: submission.e164,
            displayName: null,
            source: "opt_out_page",
            scope: "every_voice_agent",
            voiceAgentId: null,
            spokenWords: submission.message,
            author: { kind: "opt_out_page" },
        });
        return {
            accepted: true,
            accountId: submission.target.accountId,
            doNotCallEntryId: made.doNotCallEntryId,
            suppressedAt,
        };
    }
}
//# sourceMappingURL=fake-suppression.js.map